import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { audioCaptureService, AudioDevice, TranscriptionResult } from '../services/audioCaptureService';
import AudioWaveform from './AudioWaveform';
import VBCableInstructions from './VBCableInstructions';
import { conversationService } from '../services/conversationService';
import { markSessionAsSaved, resetSessionSavedStatus } from '../store/conversationSlice';

interface AudioCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTranscription?: (result: TranscriptionResult) => void;
  onCaptureStateChange?: (isCapturing: boolean) => void;
  onAudioLevel?: (level: number) => void;
  enableDualCapture?: boolean;
  isTrainingConnected?: boolean; // For training mode - indicates if training WebSocket is connected
  waitForTrainingConnection?: boolean; // If true, wait for training WebSocket before enabling recording
  onStartTrainingConnection?: () => void; // Called when user clicks "Start Recording" to initiate training connection
  /**
   * Training mode only: true once the backend has pushed the initial
   * `phase_changed` event, which means the ConversationManager is wired
   * up and ready to receive transcripts. Used to keep the modal showing
   * a "Starting session…" loader instead of the live recording UI
   * (decibel meter + stop button) while the backend is still spinning
   * up. Live-call mode leaves this undefined and the modal flips
   * straight to recording UI as soon as capture starts.
   */
  isSessionReady?: boolean;
}

const AudioCaptureModal: React.FC<AudioCaptureModalProps> = ({
  isOpen,
  onClose,
  onTranscription,
  onCaptureStateChange,
  onAudioLevel,
  enableDualCapture = false,
  isTrainingConnected = false,
  waitForTrainingConnection = false,
  onStartTrainingConnection,
  isSessionReady,
}) => {
  const dispatch = useDispatch();

  // Redux state
  const { transcripts, entries, lastSpeechRateAlert, sessionSaved } = useSelector((state: RootState) => state.conversation);
  const landmineAlerts = useSelector((state: RootState) => state.landmine?.allAlerts || []);
  const competitorAlerts = useSelector((state: RootState) => state.competitor?.allAlerts || []);

  // Capture states
  const [isCapturing, setIsCapturing] = useState(false);
  const [hasRecorded, setHasRecorded] = useState(false);
  const [isSavingSession, setIsSavingSession] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [speechRateAlerts, setSpeechRateAlerts] = useState<any[]>([]);

  // Connection status tracking
  const [isDeepgramConnected, setIsDeepgramConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  
  // Device states
  const [audioDevices, setAudioDevices] = useState<AudioDevice[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<string>('');
  const [selectedCustomerDevice, setSelectedCustomerDevice] = useState<string>('');
  const [selectedSalespersonDevice, setSelectedSalespersonDevice] = useState<string>('');
  
  // Capture mode
  const [captureMode, setCaptureMode] = useState<'microphone' | 'system-audio'>('microphone');
  
  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const devicesLoadedRef = useRef(false);
  
  // Data states
  const [error, setError] = useState<string>('');
  const [transcriptionHistory, setTranscriptionHistory] = useState<TranscriptionResult[]>([]);
  const [audioLevel, setAudioLevel] = useState(0);
  const [showVBInstructions, setShowVBInstructions] = useState(false);

  const loadAudioDevices = useCallback(async () => {
    // Prevent multiple simultaneous loads
    if (isLoadingDevices || devicesLoadedRef.current) {
      return;
    }
    
    try {
      setIsLoadingDevices(true);
      setError('');
      
      
      
      // Load microphone devices (including VB Cable)
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        console.log('✅ Microphone permission granted');
        stream.getTracks().forEach(track => track.stop());
      } catch (permissionError) {
        console.warn('⚠️ Microphone permission not granted:', permissionError);
        setError('Microphone permission required. Please allow microphone access and refresh the page.');
        return;
      }

      const devices = await audioCaptureService.getAudioDevices();
      
      setAudioDevices(devices);
      
      if (devices.length > 0) {
                 if (enableDualCapture) {
           // For dual capture, show all devices in both dropdowns
           if (devices.length > 0) {
             setSelectedCustomerDevice(devices[0].deviceId);
            
           }
           
           if (devices.length > 1) {
             setSelectedSalespersonDevice(devices[1].deviceId);
            
           }
                 } else {
           // Single device mode - select first device
           setSelectedDevice(devices[0].deviceId);
           
         }
      }
      
    } catch (error) {
      console.error('❌ Failed to load audio devices:', error);
      setError('Failed to load audio devices. Please refresh the page and try again.');
    } finally {
      setIsLoadingDevices(false);
      devicesLoadedRef.current = true;
    }
  }, [enableDualCapture, isLoadingDevices]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    
    if (isOpen) {
      // Reset the loaded flag when modal opens
      if (!devicesLoadedRef.current) {
        loadAudioDevices();
      }
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    } else {
      // Reset the flag when modal closes so devices reload next time
      devicesLoadedRef.current = false;
    }
  }, [isOpen, onClose, loadAudioDevices]);

  // Track speech rate alerts
  useEffect(() => {
    if (lastSpeechRateAlert) {
      setSpeechRateAlerts(prev => [...prev, lastSpeechRateAlert]);
    }
  }, [lastSpeechRateAlert]);

  // Monitor Deepgram connection status
  useEffect(() => {
    audioCaptureService.onConnectionStatus((status) => {
      
      setIsDeepgramConnected(status.deepgram);
    });
  }, []);

  // Check if all required connections are ready (runs when either connection status changes)
  useEffect(() => {
    if (isConnecting) {
      // Check if all required connections are ready
      let allConnected: boolean = isDeepgramConnected;

      if (waitForTrainingConnection) {
        // In training mode: require BOTH Deepgram AND training connection
        allConnected = isDeepgramConnected && isTrainingConnected;
      
      } else {
        // Normal mode: only require Deepgram
       
      }

      if (allConnected) {
      
        setIsConnecting(false);
        setIsCapturing(true);

        // Notify parent that capture is actually ready now
        if (onCaptureStateChange) {
          onCaptureStateChange(true);
        }
      }
    }
  }, [isConnecting, isDeepgramConnected, isTrainingConnected, waitForTrainingConnection, onCaptureStateChange]);

  // Connection timeout - if not connected within 15 seconds, show error
  useEffect(() => {
    if (isConnecting) {
      const timeout = setTimeout(() => {
        if (isConnecting) {
          console.error('❌ Connection timeout - services did not connect within 15 seconds');
          if (waitForTrainingConnection && !isTrainingConnected) {
            setError('Failed to connect to training service. Please check your connection and try again.');
          } else if (!isDeepgramConnected) {
            setError('Failed to connect to speech service. Please check your connection and try again.');
          }
          setIsConnecting(false);
          // Stop capture
          audioCaptureService.stopCapture();
          setIsCapturing(false);
          if (onCaptureStateChange) {
            onCaptureStateChange(false);
          }
        }
      }, 15000);

      return () => clearTimeout(timeout);
    }
  }, [isConnecting, isDeepgramConnected, isTrainingConnected, waitForTrainingConnection, onCaptureStateChange]);

  const handleStartCapture = async () => {
    
    if (enableDualCapture) {
      // Dual capture mode - always require salesperson device (microphone)
      if (!selectedSalespersonDevice) {
        setError('Please select a salesperson audio device (microphone)');
        return;
      }
    } else {
      // Single device mode
      if (!selectedDevice && captureMode === 'microphone') {
        setError('Please select an audio device');
        return;
      }
    }

    // Initiate training connection if needed (before starting Deepgram connection)
    if (waitForTrainingConnection && onStartTrainingConnection) {
      onStartTrainingConnection();
    }

    setIsLoading(true);
    setIsConnecting(true);
    setError('');

    try {
      if (enableDualCapture) {
        // For dual capture: salesperson uses microphone, customer uses system audio
        const dualConfig = {
          customerDeviceId: 'system-audio', // Special identifier for system audio
          salespersonDeviceId: selectedSalespersonDevice,
          enableCustomerCapture: true, // Always enable customer capture (system audio)
          enableSalespersonCapture: !!selectedSalespersonDevice
        };

        await audioCaptureService.startDualCapture(
          dualConfig,
          handleTranscription,
          handleAudioLevel
        );
      } else {
        // Single device mode
        if (captureMode === 'system-audio') {
        
          await audioCaptureService.startSystemAudioCapture(
            handleTranscription,
            handleAudioLevel
          );
        } else {

          await audioCaptureService.startCapture(
            selectedDevice,
            handleTranscription,
            handleAudioLevel
          );
        }
      }

      // Don't set isCapturing immediately - wait for WebSocket connections
      // The useEffect monitoring connection status will set it when ready

      setTranscriptionHistory([]);
      dispatch(resetSessionSavedStatus()); // Reset saved status when starting new recording

      // Start a new conversation session if not already started
      const sessionInfo = conversationService.getSessionInfo();
      if (!sessionInfo.sessionId) {
        // Get session info from websocket if available
        const wsInfo = (window as any).websocketService?.getSessionInfo?.();
        conversationService.startSession(wsInfo?.roomId, wsInfo?.clientId);
      }

      // Will notify parent when connection is fully ready (see useEffect above)
    } catch (error) {
      console.error('Failed to start capture:', error);
      setError(error instanceof Error ? error.message : 'Failed to start audio capture');
      setIsConnecting(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStopCapture = () => {
    audioCaptureService.stopCapture();
    setIsCapturing(false);
    setIsConnecting(false);
    setIsDeepgramConnected(false);
    setAudioLevel(0);
    setHasRecorded(true); // Mark that we have recorded something

    // Notify parent component that capture stopped
    if (onCaptureStateChange) {
      onCaptureStateChange(false);
    }
  };

  const handleTranscription = (result: TranscriptionResult) => {
    // console.log('🎯 Received transcription callback:', result);
    
    if (result.isFinal) {
      // console.log('✅ Final transcript received:', result.transcript);
      // console.log('🗣️ Speaker:', result.speaker);
      
      // Add to transcription history
      setTranscriptionHistory(prev => [...prev.slice(-4), result]);
      
      // Call parent callback if provided (for display in main transcript section)
      if (onTranscription) {
        onTranscription(result);
      }
    } else {
      // console.log('🔄 Interim transcript:', result.transcript);
    }
  };

  const handleAudioLevel = (level: number) => {
    setAudioLevel(level);
    onAudioLevel?.(level);
  };

  const handleDeviceChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const deviceId = event.target.value;
    // console.log('🔄 Changing device to:', deviceId);
    setSelectedDevice(deviceId);
  };

  const refreshDevices = () => {
    devicesLoadedRef.current = false;
    loadAudioDevices();
  };

  const handleSaveConversation = async () => {
    // Check if there are transcripts to save
    if (transcripts.length === 0) {
      alert('No conversation data to save. Please record some audio first.');
      return;
    }

    // Prompt for session name
    const sessionName = window.prompt(
      'Please enter a name for this conversation session:', 
      `Call Session - ${new Date().toLocaleString()}`
    );
    
    if (!sessionName) {
      alert('Session name is required to save the conversation.');
      return;
    }
    
    setIsSavingSession(true);

    try {
      // Save the conversation session with all detection data
      const result = await conversationService.endAndSaveSession(
        sessionName,
        transcripts,
        entries,
        speechRateAlerts,
        landmineAlerts,
        competitorAlerts
      );
      
      // Check if result indicates success - the result is the data object with sessionId
      if (result && result.success) {
        setSaveSuccess(true);
        setHasRecorded(false); // Reset the flag after saving
        dispatch(markSessionAsSaved()); // Mark session as saved in Redux
        
        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          setSaveSuccess(false);
        }, 10000);
      } else {
        console.error('Save failed - unexpected response format:', result);
        alert('Session may have been saved but response was unexpected. Please check your saved sessions.');
      }
    } catch (error) {
      console.error('Failed to save session:', error);
      alert('Failed to save conversation session. Please try again.');
    } finally {
      setIsSavingSession(false);
    }
  };

  const handleClose = () => {
    // Don't stop capture when modal is closed - allow recording to continue
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40 p-10">
      <div className="bg-white rounded-lg shadow-xl max-w-[80vw] max-h-[calc(100vh-80px)] overflow-y-auto">
        <div className="p-6 space-y-4 min-w-[600px]">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">🎧 Audio Capture Settings</h2>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${isCapturing ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
                <span className="text-sm text-gray-600">
                  {isCapturing ? 'Recording' : 'Stopped'}
                </span>
              </div>
              {/* <button
                onClick={() => setShowVBInstructions(true)}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                🎧 VB Cable Setup
              </button> */}
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>
          </div>

          {/* Device Selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Audio Device
              </label>
              <button
                onClick={refreshDevices}
                disabled={isLoadingDevices}
                className="text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400"
              >
                🔄 Refresh
              </button>
            </div>
            
            
            {isLoadingDevices ? (
              <div className="flex items-center space-x-2 py-2">
                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm text-gray-600">Loading devices...</span>
              </div>
                         ) : enableDualCapture ? (
               // Dual capture device selection
               <div className="space-y-3">
                 {/* Customer Audio - System Audio Capture */}
                 <div>
                   <label htmlFor="customer-device" className="block text-xs font-medium text-gray-600 mb-1">
                     🎧 Customer Audio (System Audio - Other Tabs)
                   </label>
                   <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                     <div className="flex items-center space-x-2">
                       <span className="text-green-500">✅</span>
                       <span className="text-sm text-green-700">
                         System audio capture enabled. You'll be prompted to select which tab/application to capture audio from.
                       </span>
                     </div>
                   </div>
                 </div>

                {/* Salesperson Device Selection */}
                <div>
                  <label htmlFor="salesperson-device" className="block text-xs font-medium text-gray-600 mb-1">
                    🎤 Salesperson Audio (Microphone)
                  </label>
                  <select
                    id="salesperson-device"
                    value={selectedSalespersonDevice}
                    onChange={(e) => setSelectedSalespersonDevice(e.target.value)}
                    disabled={isCapturing}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 text-sm"
                  >
                    <option value="">Select salesperson audio device</option>
                                         {audioDevices.map(device => (
                       <option key={device.deviceId} value={device.deviceId}>
                         {device.label}
                       </option>
                     ))}
                  </select>
                </div>
              </div>
            ) : (
              // Single device selection
              <select
                id="audio-device"
                value={selectedDevice}
                onChange={handleDeviceChange}
                disabled={isCapturing}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
              >
                {audioDevices.length === 0 ? (
                  <option value="">No devices found</option>
                ) : (
                  audioDevices.map(device => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label}
                      
                    </option>
                  ))
                )}
              </select>
            )}
            
            {audioDevices.length > 0 && (
              <div className="text-xs text-gray-500 mt-1">
                <p className="text-green-600">✅ {audioDevices.length} audio device(s) detected</p>
              </div>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <div className="flex items-center space-x-2">
                <span className="text-red-500">⚠️</span>
                <span className="text-red-700 text-sm">{error}</span>
              </div>
            </div>
          )}

          {/*
            "Live recording" UX gate. In TRAINING mode the parent passes
            isSessionReady — capture may have started but the backend
            ConversationManager isn't ready yet, so we show a "Starting
            session…" loader instead of the decibel meter / Stop button.
            In LIVE-CALL mode the prop is undefined and we fall back to
            the legacy behavior (recording UI as soon as isCapturing).
          */}
          {(() => null)()}
          {/* Control Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={isCapturing ? handleStopCapture : handleStartCapture}
              disabled={isLoading || isConnecting ||
                (isCapturing && isSessionReady === false) ||
                (enableDualCapture ? (!selectedCustomerDevice && !selectedSalespersonDevice) : !selectedDevice) ||
                isLoadingDevices}
              className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
                isCapturing
                  ? (isSessionReady === false
                      ? 'bg-blue-400 text-white cursor-wait'
                      : 'bg-red-500 hover:bg-red-600 text-white')
                  : 'bg-blue-500 hover:bg-blue-600 text-white disabled:bg-gray-300 disabled:cursor-not-allowed'
              }`}
            >
              {isLoading || isConnecting ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>
                    {isLoading ? 'Starting...' :
                     isConnecting ? (
                       waitForTrainingConnection
                         ? `Connecting... (Deepgram: ${isDeepgramConnected ? '✓' : '...'}, Training: ${isTrainingConnected ? '✓' : '...'})`
                         : `Connecting... (Deepgram: ${isDeepgramConnected ? '✓' : '...'})`
                     ) :
                     'Starting...'}
                  </span>
                </div>
              ) : isCapturing && isSessionReady === false ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Starting session…</span>
                </div>
              ) : isCapturing ? (
                '🛑 Stop Recording'
              ) : (
                enableDualCapture ? '🎙️ Start Dual Recording (Mic + System Audio)' : '🎙️ Start Recording'
              )}
            </button>

            {/* Save Conversation Button - Show after recording has stopped and not yet saved */}

          </div>

          {/* Audio Waveform — show only when we're TRULY live. In training
              that means isSessionReady === true; in live-call (undefined)
              that means isCapturing alone is enough. Without this gate
              the user saw decibel bars bouncing while the backend was
              still spinning up. */}
          {isCapturing && isSessionReady !== false && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                🎧 Audio Level
              </label>
              <AudioWaveform audioLevel={audioLevel} isRecording={isCapturing} />
            </div>
          )}

          {/* Success Message */}
          {saveSuccess && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg p-4 shadow-lg">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center animate-bounce">
                    <span className="text-white text-xl">✓</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-green-800">Recording Saved Successfully!</h3>
                  <p className="text-sm text-green-700 mt-1">
                    Your conversation has been permanently saved to the database.
                  </p>
                  <div className="mt-2 text-xs text-green-600 space-y-1">
                    <p>📝 {transcripts.length} transcripts recorded</p>
                    <p>🎯 {entries.length} questions/objections detected</p>
                    <p>📊 Session ID: {conversationService.getSessionInfo().sessionId?.slice(0, 20)}...</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Save Reminder Message */}
          {hasRecorded && !isCapturing && transcripts.length > 0 && !sessionSaved && !saveSuccess && (
            <div className="bg-green-50 border border-green-200 rounded-md p-3">
              <div className="flex items-start space-x-2">
                <span className="text-green-500">💡</span>
                <div className="text-sm text-green-700">
                  <p className="font-medium">Recording stopped successfully!</p>
                  <p className="text-xs mt-1">
                    You have {transcripts.length} transcript{transcripts.length > 1 ? 's' : ''} and {entries.length} detected question{entries.length !== 1 ? 's' : ''}.
                    Click "Save Conversation" to save this session to the database.
                  </p>
                </div>
              </div>
            </div>
          )}


          {/* Instructions */}
          <div className="border-t pt-4">
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <h4 className="text-sm font-medium text-blue-700 mb-1">Quick Setup:</h4>
              <ul className="text-xs text-blue-600 space-y-1">
                <li>• <strong>For Salesperson Audio:</strong> Select your physical microphone</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      <VBCableInstructions 
        isVisible={showVBInstructions} 
        onClose={() => setShowVBInstructions(false)} 
      />
    </div>
  );
};

export default AudioCaptureModal;